// SHCT-388 / ADM.034 — "persist on Save, not on Add". An Add click parks a
// draft node in the page store (negative `tempId`); these helpers merge it into
// the server tree for display and build the synthetic full ProItem the item
// detail form binds to before the row exists on the backend.

import type { DraftNode } from '../store/pro-item-design-page.store';
import type {
  ProDomain,
  ProItem,
  ProItemHeader,
  ProSubArea,
} from '../types/pro-item-design.types';

// Stable sentinel so ItemDetail's `[item.id, item.updatedAt]` reset effect does
// NOT re-run (and wipe the user's edits) on every keystroke while drafting.
export const DRAFT_UPDATED_AT = 'draft';

// Draft nodes carry a negative id; real server ids are always positive.
export function isDraftId(id: number): boolean {
  return id < 0;
}

function draftDomain(id: number, name: string): ProDomain {
  return { id, name, description: null, order: 0, subAreas: [] };
}

function draftSubArea(id: number, domainId: number, name: string): ProSubArea {
  return { id, domainId, name, description: null, order: 0, items: [] };
}

function draftItemHeader(
  id: number,
  domainId: number,
  subAreaId: number,
  name: string,
): ProItemHeader {
  // pid is '' until the item is created on Save (serial is BE-assigned).
  return { id, domainId, subAreaId, pid: '', name, status: 'DRAFT', order: 0, scheduleSummary: null };
}

// Returns a NEW tree with the draft node appended at the correct parent, or the
// tree unchanged when there is no draft. Auto-numbering (numbering.ts) derives
// the display number from array position, so the appended draft gets the next
// number for free.
export function mergeDraftIntoTree(
  tree: ProDomain[],
  draft: DraftNode | null,
): ProDomain[] {
  if (draft === null) return tree;
  if (draft.type === 'domain') {
    return [...tree, draftDomain(draft.tempId, draft.name)];
  }
  if (draft.type === 'subArea') {
    return tree.map((d) =>
      d.id === draft.domainId
        ? { ...d, subAreas: [...d.subAreas, draftSubArea(draft.tempId, d.id, draft.name)] }
        : d,
    );
  }
  return tree.map((d) =>
    d.id === draft.domainId
      ? {
          ...d,
          subAreas: d.subAreas.map((s) =>
            s.id === draft.subAreaId
              ? {
                  ...s,
                  items: [...s.items, draftItemHeader(draft.tempId, d.id, s.id, draft.name)],
                }
              : s,
          ),
        }
      : d,
  );
}

// A full ProItem for a draft item so ItemDetail can render before the row
// exists. Mirrors the backend createItem defaults (COMMON scope, SUM grade,
// null schedule → the form falls back to its DEFAULT_SCHEDULE).
export function buildDraftItem(
  tempId: number,
  domainId: number,
  subAreaId: number,
  name: string,
): ProItem {
  return {
    id: tempId,
    domainId,
    subAreaId,
    pid: '',
    name,
    status: 'DRAFT',
    order: 0,
    scheduleSummary: null,
    description: null,
    scope: 'COMMON',
    diseaseGroupId: null,
    diseaseCodeId: null,
    schedule: null,
    linkedQuestions: [],
    gradeMethod: 'SUM',
    gradeSum: { g1Max: 0, g2Max: 0 },
    gradeComplex: null,
    gradeGuidelines: { g1: null, g2: null, g3: null },
    updatedAt: DRAFT_UPDATED_AT,
  };
}
